import { useState, useCallback, useRef, useEffect } from 'react';
import { socket } from '../lib/socket';
import { useSocketEvent } from './useSocketEvent';
import { isMuted, getAudioContext } from './useSound';
import {
  EVENTS,
  REACTION_COOLDOWN_MS,
  REACTION_TOAST_DURATION_MS,
  SOUNDBITE_COOLDOWN_MS,
  MAX_VISIBLE_TOASTS,
  VOICE_MEMO_MAX_SECONDS,
  DEFAULT_SOUNDBITE_PRESETS,
  type ReactionPayload,
  type VoiceMemoPayload,
  type SoundbitePayload,
} from '@flip7/shared';

export interface ReactionToast {
  id: string;
  playerId: string;
  playerName: string;
  reactionId: string;
  icon?: string;
  label?: string;
  timestamp: number;
}

export interface VoiceToast {
  id: string;
  playerId: string;
  playerName: string;
  timestamp: number;
}

export interface SoundbiteToast {
  id: string;
  playerId: string;
  playerName: string;
  soundbiteId: string;
  timestamp: number;
}

export type Toast = (ReactionToast & { kind: 'reaction' }) | (VoiceToast & { kind: 'voice' }) | (SoundbiteToast & { kind: 'soundbite' });

let toastCounter = 0;

// Soundbite playback via Audio elements with queuing
let currentSoundbiteAudio: HTMLAudioElement | null = null;
let currentSoundbiteId: string | null = null;
let soundbiteQueue: string[] = [];
let onSoundbiteStop: (() => void) | null = null;
let onSoundbiteChange: ((id: string | null) => void) | null = null;

function playNextInQueue() {
  if (soundbiteQueue.length === 0) {
    currentSoundbiteAudio = null;
    currentSoundbiteId = null;
    onSoundbiteStop?.();
    onSoundbiteChange?.(null);
    return;
  }

  const nextId = soundbiteQueue.shift()!;
  const preset = DEFAULT_SOUNDBITE_PRESETS[nextId];
  if (!preset) { playNextInQueue(); return; }

  const audio = new Audio(preset.url);
  audio.onended = () => playNextInQueue();
  audio.play().catch(() => playNextInQueue());
  currentSoundbiteAudio = audio;
  currentSoundbiteId = nextId;
  onSoundbiteChange?.(nextId);
}

function stopSoundbite() {
  soundbiteQueue = [];
  if (currentSoundbiteAudio) {
    currentSoundbiteAudio.pause();
    currentSoundbiteAudio = null;
  }
  currentSoundbiteId = null;
  onSoundbiteStop?.();
  onSoundbiteChange?.(null);
}

function playSoundbite(soundbiteId: string) {
  if (isMuted()) return;

  const preset = DEFAULT_SOUNDBITE_PRESETS[soundbiteId];
  if (!preset) return;

  // If something is already playing, queue it
  if (currentSoundbiteAudio) {
    soundbiteQueue.push(soundbiteId);
    return;
  }

  const audio = new Audio(preset.url);
  audio.onended = () => playNextInQueue();
  audio.play().catch(() => playNextInQueue());
  currentSoundbiteAudio = audio;
  currentSoundbiteId = soundbiteId;
  onSoundbiteChange?.(soundbiteId);
}

export function useReactions(roomCode: string | null) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [cooldown, setCooldown] = useState(false);
  const [soundbiteCooldown, setSoundbiteCooldown] = useState(false);
  const [playingSoundbite, setPlayingSoundbite] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Wire up soundbite callbacks to update React state
  useEffect(() => {
    onSoundbiteStop = () => setPlayingSoundbite(null);
    onSoundbiteChange = (id) => setPlayingSoundbite(id);
    return () => { onSoundbiteStop = null; onSoundbiteChange = null; };
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop();
        recorderRef.current.stream.getTracks().forEach(t => t.stop());
      }
      if (recordTimerRef.current) clearTimeout(recordTimerRef.current);
    };
  }, []);

  const addToast = useCallback((toast: Toast) => {
    setToasts(prev => {
      const next = [...prev, toast];
      return next.slice(-MAX_VISIBLE_TOASTS);
    });
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== toast.id));
    }, REACTION_TOAST_DURATION_MS);
  }, []);

  // Listen for reactions
  useSocketEvent<ReactionPayload>(EVENTS.REACTION, (data) => {
    addToast({
      kind: 'reaction',
      id: `r-${++toastCounter}`,
      playerId: data.playerId,
      playerName: data.playerName,
      reactionId: data.reactionId,
      icon: data.icon,
      label: data.label,
      timestamp: Date.now(),
    });
  });

  // Listen for voice memos
  useSocketEvent<VoiceMemoPayload>(EVENTS.VOICE_MEMO, (data) => {
    addToast({
      kind: 'voice',
      id: `v-${++toastCounter}`,
      playerId: data.playerId,
      playerName: data.playerName,
      timestamp: Date.now(),
    });

    // Auto-play via shared Web Audio API context
    if (!isMuted()) {
      try {
        const binaryStr = atob(data.audio);
        const bytes = new Uint8Array(binaryStr.length);
        for (let i = 0; i < binaryStr.length; i++) {
          bytes[i] = binaryStr.charCodeAt(i);
        }

        const ctx = getAudioContext();
        if (!ctx) return;

        const playAudio = async () => {
          if (ctx.state === 'suspended') {
            await ctx.resume();
          }
          try {
            // Pass a copy — decodeAudioData detaches the original buffer
            const buffer = await ctx.decodeAudioData(bytes.buffer.slice(0));
            const source = ctx.createBufferSource();
            source.buffer = buffer;
            source.connect(ctx.destination);
            source.start(0);
          } catch {
            // decodeAudioData failed — fallback to Audio element
            // Try both MIME types since sender format varies by browser
            for (const mime of ['audio/webm', 'audio/mp4', 'audio/ogg']) {
              const blob = new Blob([bytes], { type: mime });
              const url = URL.createObjectURL(blob);
              const audio = new Audio(url);
              try {
                await audio.play();
                audio.onended = () => URL.revokeObjectURL(url);
                return;
              } catch {
                URL.revokeObjectURL(url);
              }
            }
          }
        };
        playAudio();
      } catch {
        // Decode error — ignore
      }
    }
  });

  // Listen for soundbites
  useSocketEvent<SoundbitePayload>(EVENTS.SOUNDBITE, (data) => {
    addToast({
      kind: 'soundbite',
      id: `s-${++toastCounter}`,
      playerId: data.playerId,
      playerName: data.playerName,
      soundbiteId: data.soundbiteId,
      timestamp: Date.now(),
    });
    playSoundbite(data.soundbiteId);
    setPlayingSoundbite(data.soundbiteId);
  });

  const sendReaction = useCallback((reactionId: string, icon?: string, label?: string) => {
    if (!roomCode || cooldown) return;
    socket.emit(EVENTS.SEND_REACTION, { roomCode, reactionId, icon, label });
    setCooldown(true);
    setTimeout(() => setCooldown(false), REACTION_COOLDOWN_MS);
  }, [roomCode, cooldown]);

  const toggleSoundbite = useCallback((soundbiteId: string) => {
    // If this soundbite is already playing, stop it locally
    if (currentSoundbiteId === soundbiteId) {
      stopSoundbite();
      return;
    }
    if (!roomCode || soundbiteCooldown) return;
    socket.emit(EVENTS.SEND_SOUNDBITE, { roomCode, soundbiteId });
    setSoundbiteCooldown(true);
    setTimeout(() => setSoundbiteCooldown(false), SOUNDBITE_COOLDOWN_MS);
  }, [roomCode, soundbiteCooldown]);

  const startRecording = useCallback(async () => {
    if (!roomCode || isRecording) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Prefer webm/opus, fallback to whatever is available
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : '';

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        setIsRecording(false);

        if (chunksRef.current.length === 0) return;

        const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = (reader.result as string).split(',')[1];
          if (base64 && roomCode) {
            socket.emit(EVENTS.SEND_VOICE_MEMO, { roomCode, audio: base64 });
          }
        };
        reader.readAsDataURL(blob);
      };

      recorder.start();
      recorderRef.current = recorder;
      setIsRecording(true);

      // Auto-stop after max duration
      recordTimerRef.current = setTimeout(() => {
        if (recorderRef.current && recorderRef.current.state === 'recording') {
          recorderRef.current.stop();
        }
      }, VOICE_MEMO_MAX_SECONDS * 1000);
    } catch {
      // Mic permission denied or unavailable
    }
  }, [roomCode, isRecording]);

  const stopRecording = useCallback(() => {
    if (recordTimerRef.current) clearTimeout(recordTimerRef.current);
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop();
    }
  }, []);

  return { toasts, isRecording, cooldown, soundbiteCooldown, playingSoundbite, sendReaction, toggleSoundbite, startRecording, stopRecording };
}
